import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { HelpCircle, Mail, ArrowRight } from "lucide-react";

const Support = () => {
  const faqs = {
    pricing: [
      {
        question: "What payment methods do you accept?",
        answer: "We accept all major credit cards and debit cards. Enterprise customers can also pay via invoice."
      },
      {
        question: "Can I change my plan at any time?",
        answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately."
      },
      {
        question: "Is there a free trial?",
        answer: "Yes, you can start with our free plan to test the basic features. No credit card required."
      }
    ],
    features: [
      {
        question: "How long can my meetings be?",
        answer: "Meeting duration varies by plan: Free (30 min), Basic (60 min), Pro (90 min), and Premium (120 min)."
      },
      {
        question: "How does the AI transcription work?",
        answer: "Our AI automatically transcribes your meeting audio into text, with advanced features like speaker identification available on higher-tier plans."
      },
      {
        question: "What happens to my data?",
        answer: "Your data is securely stored and retained based on your plan. Free users get 30 days retention, while Premium users get unlimited retention."
      }
    ],
    troubleshooting: [
      {
        question: "Why isn't my audio recording working?",
        answer: "First, ensure you've granted microphone permissions in your browser. If issues persist, try using a different browser or check our browser compatibility list."
      },
      {
        question: "How can I recover deleted meetings?",
        answer: "Premium and Pro users can recover deleted meetings within their retention period. Contact support for assistance."
      },
      {
        question: "What should I do if transcription fails?",
        answer: "If transcription fails, try re-uploading the audio file. If the issue persists, contact our support team with the meeting ID."
      }
    ]
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left column - FAQs (2/3 width) */}
        <div className="lg:w-2/3">
          <div className="flex items-center gap-2 mb-8">
            <HelpCircle className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">Support Center</h1>
          </div>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">Pricing & Billing</h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.pricing.map((faq, index) => (
                  <AccordionItem key={index} value={`pricing-${index}`}>
                    <AccordionTrigger>{faq.question}</AccordionTrigger>
                    <AccordionContent>{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Features</h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.features.map((faq, index) => (
                  <AccordionItem key={index} value={`features-${index}`}>
                    <AccordionTrigger>{faq.question}</AccordionTrigger>
                    <AccordionContent>{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Troubleshooting</h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.troubleshooting.map((faq, index) => (
                  <AccordionItem key={index} value={`troubleshooting-${index}`}>
                    <AccordionTrigger>{faq.question}</AccordionTrigger>
                    <AccordionContent>{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>
        </div>

        {/* Right column - Contact Us (1/3 width) */}
        <div className="lg:w-1/3">
          <div className="bg-card rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Need Help?</h2>
            </div>
            <p className="text-muted-foreground mb-6">
              Can't find what you're looking for? Our support team is here to help you with any questions or concerns.
            </p>
            <Button asChild className="w-full group">
              <Link to="/contact" className="flex items-center justify-center gap-2">
                Contact Support
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;